<script setup lang="ts">
import { CookiesKeys } from '@/constants/enums/CookiesEnum';
import { LocalStorageKeys } from '@/constants/enums/LocalStorageEnum';
import { RuleEnum } from '@/constants/enums/RuleEnum';
import type { ThirdPartySMS } from '@/models/interface/auth';
import type { SignupData } from '@/models/interface/register';
import { api } from '@/services';
import { isApiSuccess, resolveApiData } from '@/utils/api/functions';
import { UtilCommon } from '@/utils/utilCommon';
import WrongAlert from './errorAlert/WrongAlert.vue';
import WrongMessage from './errorAlert/WrongMessage.vue';
import WrongSignupPhone from './errorAlert/WrongSignupPhone.vue';
import WrongVerificationCode from './errorAlert/WrongVerificationCode.vue';
import FormCard from './formElement/FormCard.vue';
import FormInput from './formElement/FormInput.vue';

const router = useRouter();
const user = useUserStore();

const formChoose = defineModel('formChoose');
const showPopup = defineModel('showPopup');
const showAlert = defineModel('showAlert');
const information = defineModel<SignupData>('information', { required: true });
const inputRefVerificationCode = ref<InstanceType<typeof FormInput>>();

const { timeLeft, isCounting, startCountdown, resetTime } = useCountdown();
const verifyCodeText = ref<string>('重發');
const isSubmit = ref<boolean>(false);

const { fetchData } = useRegister();

const sendVerifyCode = async () => {
  const adId = UtilCommon.getCookie(CookiesKeys.AdId);
  resetTime();
  startCountdown();
  information.value.verificationCode = '';

  const type = window.localStorage.getItem('from-third-party-type');

  const params: ThirdPartySMS = {
    phone: information.value.phone,
    email: window.localStorage.getItem('from-third-party-email') ?? '',
    fullName: information.value.fullName,
    thirdPartyId: window.localStorage.getItem('from-third-party-id') ?? '',
    type: type ? parseInt(type) : 5
  };
  if (adId && adId != null) {
    params.ad_id = parseInt(adId);
  }
  const res = await api.auth.thirdPartySMSVerification(params);

  if (isApiSuccess(res)) return;

  resetTime();

  if (res.data.errorCode === -4020 || res.data.errorCode === -4038) {
    showPopup.value = true;
    showAlert.value = WrongSignupPhone;
    return;
  }

  showPopup.value = true;
  showAlert.value = WrongMessage;
};

const handleSubmit = async () => {
  isSubmit.value = true;
  const checked = inputRefVerificationCode.value?.handleBlur() ?? false;

  if (!checked) {
    isSubmit.value = false;
    return;
  }

  const mgm = UtilCommon.getLocalStorage<string>(LocalStorageKeys.MGMCode);
  const adId = UtilCommon.getCookie(CookiesKeys.AdId);
  const type = window.localStorage.getItem('from-third-party-type');

  const res = await api.auth.thirdPartyRegister({
    ...information.value,
    email: window.localStorage.getItem('from-third-party-email') ?? '',
    thirdPartyId: window.localStorage.getItem('from-third-party-id') ?? '',
    type: type ? parseInt(type) : 5,
    sex: parseInt(information.value.sex),
    roleType: 0,
    ad_id: adId ? parseInt(adId) : 0,
    refMGMCode: mgm ?? ''
  });

  if (isApiSuccess(res)) {
    user.token = resolveApiData(res);
    window.localStorage.removeItem('from-third-party-email');
    window.localStorage.removeItem('from-third-party-id');
    window.localStorage.removeItem('from-third-party-type');

    router.push({ name: 'thankYou' });
    return;
  }

  isSubmit.value = false;
  if (res.data.errorCode === -4038 || res.data.errorCode === -4061) {
    showPopup.value = true;
    showAlert.value = WrongSignupPhone;
    return;
  }
  if (res.data.errorCode === -4035 || res.data.errorCode === -4047) {
    showPopup.value = true;
    showAlert.value = WrongVerificationCode;
    return;
  }
  showPopup.value = true;
  showAlert.value = WrongMessage;
};

const handleClick = async () => {
  fetchData({ tel: information.value.phone });

  showPopup.value = true;
  showAlert.value = WrongAlert;
};

onMounted(() => {
  startCountdown();
});
</script>

<template>
  <FormCard>
    <div class="title-container">
      <p>簡訊驗證碼</p>
      <div class="cross-btn" @click="formChoose = ''">
        <SvgIcon color="grey" name="cross" />
      </div>
    </div>

    <div class="verify-container">
      <FormInput
        ref="inputRefVerificationCode"
        inputPlaceHolder="請輸入簡訊驗證碼"
        inputTitle=""
        inputType="text"
        :verifyRule="RuleEnum.VerificationCode"
        v-model:inputValue="information.verificationCode"
        style="margin: 0"
        canDeleteAll
      >
        <p class="verify-hint">超過30分鐘，驗證碼即失效</p>
      </FormInput>

      <button
        v-if="information.verificationCode.trim() === '' || !isCounting"
        class="send-verify-code"
        :class="{ isCounting: isCounting }"
        @click="sendVerifyCode"
        :disabled="isSubmit"
      >
        {{ isCounting ? `${timeLeft}s` : verifyCodeText }}
      </button>
    </div>

    <div class="submit" :class="{ isSubmit: isSubmit }" @click="handleSubmit">
      <p>免費加入</p>
      <svg
        v-if="isSubmit"
        width="21"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="animate-spin ml-1.5"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M9.75 1C9.75 0.585786 10.0858 0.25 10.5 0.25C15.8848 0.25 20.25 4.61522 20.25 10C20.25 15.3848 15.8848 19.75 10.5 19.75C5.11522 19.75 0.75 15.3848 0.75 10C0.75 9.58579 1.08579 9.25 1.5 9.25C1.91421 9.25 2.25 9.58579 2.25 10C2.25 14.5564 5.94365 18.25 10.5 18.25C15.0564 18.25 18.75 14.5564 18.75 10C18.75 5.44365 15.0564 1.75 10.5 1.75C10.0858 1.75 9.75 1.41421 9.75 1Z"
          fill="white"
        />
      </svg>
    </div>

    <p class="no-verify" @click="handleClick">一直未收到驗證碼?</p>
  </FormCard>
</template>

<style lang="scss" scoped>
/** style */
.title-container {
  display: flex;
  width: 100%;
  align-items: center;
  position: relative;
  margin-bottom: 16px;
  p {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000cc;
    font-size: 18px;
    font-weight: 600;
  }
  .cross-btn {
    cursor: pointer;
  }
}
.verify-container {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: self-start;
  gap: 8px;

  .send-verify-code {
    border-radius: 4px;
    margin-top: 4px;
    width: 102px;
    flex-shrink: 0;
    white-space: nowrap;
    height: 48px;
    color: $txt-color-grey-80;
    font-size: 18px;
    background-color: #f4f4f4;
    border: none;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }
  .isCounting {
    user-select: none;
    pointer-events: none;
  }
  .verify-hint {
    color: $txt-color-grey-24;
    font-size: 12px;
    width: 100%;
    padding: 8px;
  }
}
.submit {
  margin: 16px 0;
  background-color: #f74768;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #ffffff;
  height: 40px;
  width: 100%;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  &.isSubmit {
    user-select: none;
    pointer-events: none;
  }
}
.no-verify {
  color: #f74768;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: center;
  width: 100%;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

.junior-landing-page > .main-content > .card > .submit {
  background-color: $color-brownish-100;
}

.junior-landing-page > .main-content > .card > .no-verify {
  color: $color-brownish-100;
}
</style>
